import { useEffect } from "react";
import type { ReactNode } from "react";
import { Container } from "../components/Container";
import { SectionHeading } from "../components/SectionHeading";
import styles from "./StaticPage.module.css";

/**
 * The layout every static page shares (`WEB-018`).
 *
 * `docs/ARCHITECTURE.md` § Public web puts "legal/static pages" in this repo, and they are
 * all the same kind of thing: a prose document under the ordinary chrome, reading nothing
 * and holding no state. So there is one layout rather than one per document — `WEB-019`'s
 * Terms and Privacy pages are two more documents inside this shell, not two more page
 * designs.
 *
 * It is deliberately a *document*, not a section of the board. The board's own furniture is
 * carried across — the brass rule under each heading, the page gutter, the type scale — but
 * the column is capped at `--measure` and the title sits a step below the hero scale,
 * because this is a page somebody reads a paragraph at a time rather than scans.
 *
 * The title is set on the document too. Cloudflare serves the same shell for every path, so
 * `index.html`'s title is the homepage's; a static page that did not set its own would be
 * bookmarked, and read aloud, as the board.
 */
export function StaticPage({
  kicker,
  title,
  documentTitle,
  lede,
  children,
}: {
  kicker: string;
  title: string;
  /** The browser title. Defaults to the page title with the site name after it. */
  documentTitle?: string | undefined;
  lede: ReactNode;
  children: ReactNode;
}) {
  const browserTitle = documentTitle ?? `${title} — Dealbid`;

  useEffect(() => {
    document.title = browserTitle;
  }, [browserTitle]);

  return (
    <main id="main" className={styles.page}>
      <Container>
        <p className={styles.kicker}>{kicker}</p>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.lede}>{lede}</p>
        <div className={styles.body}>{children}</div>
      </Container>
    </main>
  );
}

/**
 * One ruled section of a static document.
 *
 * `SectionHeading` rather than a bare `h2` so a policy page is ruled in brass like every
 * other heading on the site: this is Dealbid stating its own rules, and it should look like
 * the rest of the product saying it rather than like boilerplate pasted underneath.
 */
export function StaticSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className={styles.section} aria-labelledby={id}>
      <SectionHeading id={id} title={title} />
      <div className={styles.prose}>{children}</div>
    </section>
  );
}

/**
 * An aside inside a document section — a claim's limits, set apart from the claim.
 *
 * It is deliberately *not* `Callout`. That primitive is the shared surface for empty and
 * error states and announces itself as a live region, which is right for something that
 * just happened to a reader and wrong for a paragraph that has been on the page all along:
 * a screen reader would read the policy's fine print out over whatever else was being said.
 * Same left-ruled shape, no announcement.
 */
export function StaticNote({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={styles.note}>
      <p className={styles.noteTitle}>{title}</p>
      <div className={styles.noteBody}>{children}</div>
    </div>
  );
}
